import * as React from 'react';
import { useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import OrgAvatar from 'src/shared/components/ui/icons/org-avatar';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from 'src/shared/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from 'src/shared/components/ui/popover';
import { useOrg } from 'src/shared/contexts/OrgContext';
import { useOrgs } from 'src/shared/queries/useOrgs';
import { cn } from 'src/shared/lib/utils';

// disable the org picker if domain is one of the following
const defaultDomains = ['localhost:3000', 'app.tekst.ai', 'app.tekst.com', 'preview.tekst.com'];

type OrgSwitcherProps = {
  expanded: boolean;
  domain: string;
};

export default function OrgSwitcher({ expanded, domain }: OrgSwitcherProps) {
  const { data: orgs } = useOrgs();
  const { currentOrg, switchOrg } = useOrg();
  const [open, setOpen] = useState(false);

  if (!currentOrg.id) {
    return expanded ? (
      <div className="px-2 py-1 ml-1 w-full">
        <div className="h-5 w-24 animate-pulse rounded bg-neutral-200" />
      </div>
    ) : (
      <div className="w-full flex-center">
        <div className="h-7 w-7 animate-pulse rounded-full bg-neutral-200" />
      </div>
    );
  }

  // Check if domain is a default domain or a preview domain
  const decodedDomain = decodeURIComponent(domain);
  const isDefaultOrPreviewDomain =
    defaultDomains.includes(decodedDomain) || decodedDomain.endsWith('.preview.tekst.com');

  // Prevent hydration mismatch: during loading, assume we might have multiple orgs
  // Only show the simplified version after we know for certain there's only one org
  const shouldShowSimpleVersion = !isDefaultOrPreviewDomain || (orgs && orgs.length < 2);

  if (shouldShowSimpleVersion) {
    // no org switcher when on company domain or no other org available
    return expanded ? (
      <p className="max-w-full group text-sm rounded-sm font-medium truncate flex items-center gap-1 w-full px-2 py-1 ml-1">
        {currentOrg.name}
      </p>
    ) : (
      <div className="w-full flex-center">
        <OrgAvatar size={28} org={currentOrg} />
      </div>
    );
  }

  return (
    <div className="w-full overflow-hidden">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          {expanded ? (
            <div className="select-none group text-sm rounded-sm font-medium truncate flex items-center justify-between gap-1 w-full cursor-pointer hover:bg-neutral-100 px-2 py-1.5 ml-1 data-[state-open]:border">
              <p className="max-w-full truncate">{currentOrg.name}</p>
              <ChevronDown size={14} className="shrink-0 opacity-0 group-hover:opacity-100" />
            </div>
          ) : (
            <div className="cursor-pointer hover:opacity-80 transition-all w-full flex-center">
              <OrgAvatar size={28} org={currentOrg} />
            </div>
          )}
        </PopoverTrigger>
        <PopoverContent className="w-[230px] max-h-full p-0 m-0" align="start" alignOffset={expanded ? -48 : 15}>
          <Command className="max-h-full">
            <CommandList className="max-h-full">
              <CommandInput className="h-10" placeholder="Search organisation..." />
              <CommandEmpty>No organisations found.</CommandEmpty>
              <CommandGroup key="Current Org" heading="Current organisation">
                <CommandItem onSelect={() => setOpen(false)} key={currentOrg.id} className="gap-2 cursor-pointer">
                  <OrgAvatar org={currentOrg} />
                  <div className="overflow-hidden whitespace-nowrap overflow-ellipsis">{currentOrg.name}</div>
                  <Check className={cn('ml-auto h-4 w-4 shrink-0 opacity-100')} />
                </CommandItem>
              </CommandGroup>
              <CommandSeparator />
              <CommandGroup
                key="Orgs"
                heading={`Other ( ${orgs?.filter((org) => org.id !== currentOrg.id).length} )`}
                className="overflow-y-auto max-h-80"
              >
                <div>
                  {orgs
                    ?.filter((org) => org.id !== currentOrg.id)
                    .sort((a, b) => {
                      if (!a.name && !b.name) {
                        return 0;
                      }
                      if (!a.name) {
                        return 1;
                      }
                      if (!b.name) {
                        return -1;
                      }
                      return a.name.localeCompare(b.name);
                    })
                    .map((org) => (
                      <CommandItem
                        key={org.id}
                        value={org.name}
                        onSelect={() => {
                          switchOrg(org.id);
                          setOpen(false);
                        }}
                        className="gap-2 cursor-pointer"
                      >
                        {org && <OrgAvatar org={org} />}
                        <div className="overflow-hidden whitespace-nowrap overflow-ellipsis">{org.name}</div>
                        <Check
                          className={cn(
                            'ml-auto h-4 w-4 shrink-0',
                            currentOrg.id === org.id ? 'opacity-100' : 'opacity-0'
                          )}
                        />
                      </CommandItem>
                    ))}
                </div>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
